import React, { useState, ReactElement } from 'react'
import * as S from './styled'
import Icon from '@ui-components/icon'

export type TableColumn<T> = {
    label: string | React.ReactNode
    dataIndex: string
    width?: number
    onClick?: (rowId: string) => void
    render?: (text: T[keyof T] | string, row: T) => React.ReactNode
}
export interface ITableItem {
    id: number | string
    children: ITableItem[]
}

interface IProps<T> {
    data: T[]
    columns: TableColumn<T>[]
    onClickRow?: (value?: any) => void
    onSelectRow?: (ids: Set<number | string>) => void
    isSidebar?: boolean
    collapserAddon?: React.ReactNode
    filterProps?: {
        term: string
        column: string
    }
}

interface IRow {
    prevLevel?: number
    display?: boolean
}

interface IRowInstance<T> extends IRow {
    level: number,
    row: T,
    rowIndex: number
}

interface ICell<T> {
    rowValues: T
    value: T[keyof T] | string
    level: number
    expandable: boolean
    expand: boolean
    index: number
    collapserAddon?: React.ReactNode
    title: string
    render?: (text: T[keyof T] | string, row: T) => React.ReactNode
    toggleExpand: () => void
}

const Cell = <T extends ITableItem>({
    rowValues,
    value = '-',
    expandable,
    expand,
    level,
    index,
    collapserAddon,
    title,
    render,
    toggleExpand,
}: ICell<T>
): any => {

    const props = () => {
        if (expandable) {
            return {
                style: { display: 'flex', alignItems: 'center', paddingLeft: level * 15 },
                onClick: toggleExpand
            }
        }
    }

    const collapser = () => {
        return (
            <>
                <Icon
                    type={'caret-down'}
                    style={{ fontWeight: 600, marginRight: 5, cursor: 'pointer' }}
                    rotate={!expand ? -90 : 0}
                />
                {collapserAddon}
            </>

        )

    }

    const getContent = () => {
        const displayValue = render ? render(value, rowValues) : value

        return (
            <div {...props()} title={title} >
                {expandable && (
                    collapser()
                )}
                {displayValue}
            </div>
        )
    }

    return (
        <td style={{ padding: "10px 12px"}}>
            {getContent()}
        </td>
    )
}

const RowInstance = <T extends ITableItem>({ level, row, rowIndex, columns, collapserAddon, display }: IProps<T> & IRowInstance<T>) => {
    const [expand, setExpand] = useState(true)

    const toggleExpand = () => {
        setExpand(!expand)
    }

    return (
        <>
            <tr
                style={{ display: `${display === false ? 'none' : 'table-row'}` }}
                className="table-row"
                key={row.id}
            >
                {columns.map((column, columnIndex) => {
                    return (
                        <Cell
                            title={row[column.dataIndex as keyof T]}
                            level={level}
                            index={columnIndex}
                            expandable={!!row.children && columnIndex === 0}
                            expand={expand}
                            toggleExpand={toggleExpand}
                            key={`${rowIndex}-${columnIndex}`}
                            rowValues={row}
                            value={row[column.dataIndex as keyof T]}
                            render={column.render}
                            collapserAddon={collapserAddon}
                        />
                    )
                })}
            </tr>
            {row.children && (
                <Rows display={display && expand} data={row.children as T[]} columns={columns} collapserAddon={collapserAddon} prevLevel={level} />
            )}
        </>
    )
}

const Rows = <T extends ITableItem>({ data, columns, prevLevel, collapserAddon, display = true }: IProps<T> & IRow): any => {
    const level = prevLevel == undefined ? 0 : prevLevel + 1

    return data.map((row, rowIndex): any => {
        return <RowInstance key={`${level}-${rowIndex}`} level={level} row={row} display={display} rowIndex={rowIndex} data={row.children} columns={columns} collapserAddon={collapserAddon} prevLevel={level} />
    })

}

const TreeTable = <T extends ITableItem>(props: IProps<T>): ReactElement<IProps<T>> => {
    const { data, columns, collapserAddon } = props;

    return (
        <S.Table>
            <thead>
                <tr className="table-row">
                    {columns.map((column, index) => (
                        <th 
                            style={{width: column.width ? column.width : undefined}}
                            key={index}
                        >
                                {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                <Rows data={data} columns={columns} collapserAddon={collapserAddon}/>
            </tbody>
        </S.Table>
    )
}

export default TreeTable
